import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Loader, TrendingUp, Trophy, ChevronRight,
  ArrowRight, Sparkles, Target,
} from "lucide-react";
import { api } from "../../lib/api";
import Collapsible from "./Collapsible";

const ORANGE = "#FF6A00";
const NAVY = "#0F1B2D";
const GOLD = "#CA8A04";
const GREEN = "#0E5A4A";

const OUTCOME_COPY = {
  applied:     { label: "Applied",     color: "#475569", bg: "#F1F5F9" },
  interview:   { label: "Interview",   color: NAVY,      bg: "#FEF3C7" },
  offer:       { label: "Offer",       color: GREEN,     bg: "#D1FAE5" },
  rejection:   { label: "Rejection",   color: "#991B1B", bg: "#FEE2E2" },
  no_response: { label: "Ghosted",     color: "#78350F", bg: "#FFE4D2" },
};

/**
 * MultiResumeSummary — collapsible "Resume portfolio" widget on the Landing
 * dashboard. Aggregates fit scores and outcomes across every tailored resume
 * the user has run, so they can see at a glance which roles are working,
 * which need a re-tailor, and where to focus next.
 *
 * Data source: GET /api/landing/resume-summary
 */
export default function MultiResumeSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/landing/resume-summary");
        if (alive) setSummary(data);
      } catch { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Empty state — no analyses yet → hide the whole collapsible
  if (!loading && (!summary || summary.total_analyses === 0)) return null;

  const eyebrow = loading
    ? "Loading your portfolio…"
    : `${summary.total_analyses} resume${summary.total_analyses === 1 ? "" : "s"} · ${summary.qualifying_analyses} scored 60+`;

  return (
    <Collapsible
      testid="resume-portfolio-collapsible"
      title="Resume portfolio"
      eyebrow={eyebrow}
      accent={NAVY}
      headerBg="linear-gradient(135deg, #FEF3C7 0%, #FED7AA 100%)"
      eyebrowColor={NAVY}
      titleColor={NAVY}
      defaultOpen={false}
    >
      {loading ? (
        <div className="py-8 flex items-center justify-center gap-2"
             data-testid="resume-portfolio-loading">
          <Loader size={16} className="animate-spin" style={{ color: ORANGE }} />
          <span className="font-mono-rm text-[11px] uppercase tracking-widest">Loading…</span>
        </div>
      ) : (
        <PortfolioBody summary={summary} />
      )}
    </Collapsible>
  );
}

function PortfolioBody({ summary }) {
  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="resume-portfolio-panel">
      {/* Top-line stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5"
           data-testid="resume-portfolio-stats">
        <StatTile
          label="Total resumes"
          value={summary.total_analyses}
          accent={NAVY}
          icon={<FileText size={14} />}
          testid="stat-total"
        />
        <StatTile
          label="Average fit"
          value={`${summary.average_fit}/100`}
          accent={ORANGE}
          icon={<TrendingUp size={14} />}
          testid="stat-avg-fit"
        />
        <StatTile
          label="Top score"
          value={summary.top_score ? `${summary.top_score.score}/100` : "—"}
          subtitle={summary.top_score ? `${summary.top_score.role}` : null}
          accent={GOLD}
          icon={<Trophy size={14} />}
          testid="stat-top"
        />
        <StatTile
          label="Active pipeline"
          value={summary.active_in_pipeline}
          subtitle={`${summary.interview_rate}% interview rate`}
          accent={GREEN}
          icon={<Target size={14} />}
          testid="stat-pipeline"
        />
      </div>

      {/* Fit-buckets */}
      <div className="rm-card-static p-3"
           style={{ background: "white", borderColor: "#E2E8F0" }}
           data-testid="resume-portfolio-buckets">
        <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
             style={{ color: NAVY, fontWeight: 700 }}>
          · Fit score distribution
        </div>
        <BucketRow label="90+ · Strong" count={summary.fit_buckets["90+"]} total={summary.total_analyses} color={GREEN} testid="bucket-90" />
        <BucketRow label="75-89 · Solid" count={summary.fit_buckets["75-89"]} total={summary.total_analyses} color={ORANGE} testid="bucket-75" />
        <BucketRow label="60-74 · Workable" count={summary.fit_buckets["60-74"]} total={summary.total_analyses} color={GOLD} testid="bucket-60" />
        <BucketRow label="< 60 · Re-tailor" count={summary.fit_buckets["<60"]} total={summary.total_analyses} color="#991B1B" testid="bucket-low" />
      </div>

      {/* Outcomes */}
      {Object.values(summary.outcomes || {}).some((n) => n > 0) && (
        <div className="rm-card-static p-3"
             style={{ background: "white", borderColor: "#E2E8F0" }}
             data-testid="resume-portfolio-outcomes">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
               style={{ color: NAVY, fontWeight: 700 }}>
            · Outcomes logged
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.outcomes).map(([key, count]) => {
              if (count === 0) return null;
              const o = OUTCOME_COPY[key] || OUTCOME_COPY.applied;
              return (
                <span key={key}
                      className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1 rounded inline-flex items-center gap-1"
                      style={{ background: o.bg, color: o.color, border: `1px solid ${o.color}`, fontWeight: 700 }}
                      data-testid={`outcome-${key}`}>
                  {o.label} · {count}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent analyses list */}
      {(summary.recent_analyses || []).length > 0 && (
        <div className="rm-card-static p-3"
             style={{ background: "white", borderColor: "#E2E8F0" }}
             data-testid="resume-portfolio-recent">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
               style={{ color: NAVY, fontWeight: 700 }}>
            · Recent tailored resumes
          </div>
          <ul className="space-y-1.5">
            {summary.recent_analyses.map((r) => (
              <li key={r.id}>
                <Link to={`/results/${r.id}`}
                      className="flex items-center justify-between gap-3 px-2.5 py-2 rounded hover:bg-slate-50 transition"
                      data-testid={`recent-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display uppercase text-sm leading-tight truncate"
                         style={{ color: NAVY, letterSpacing: "-0.01em" }}>
                      {r.role}
                    </div>
                    <div className="text-[11px] truncate" style={{ color: "#475569" }}>
                      {r.company}{r.outcome ? ` · ${(OUTCOME_COPY[r.outcome] || {}).label || r.outcome}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {typeof r.fit === "number" && (
                      <span className="font-mono-rm uppercase text-[10px] tracking-widest px-2 py-0.5 rounded"
                            style={{
                              background: r.fit >= 75 ? "#D1FAE5" : r.fit >= 60 ? "#FEF3C7" : "#FEE2E2",
                              color: r.fit >= 75 ? GREEN : r.fit >= 60 ? GOLD : "#991B1B",
                              border: `1px solid ${r.fit >= 75 ? GREEN : r.fit >= 60 ? GOLD : "#991B1B"}`,
                              fontWeight: 700,
                            }}>
                        {r.fit}/100
                      </span>
                    )}
                    <ChevronRight size={14} style={{ color: "#94A3B8" }} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top-score CTA */}
      {summary.top_score && (
        <div className="rm-card-static p-3 flex items-center justify-between gap-3 flex-wrap"
             style={{
               background: `linear-gradient(135deg, #FBF7EE 0%, #FFE4D2 100%)`,
               borderColor: NAVY,
               boxShadow: `3px 3px 0 0 ${ORANGE}`,
             }}
             data-testid="resume-portfolio-top-cta">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles size={14} style={{ color: ORANGE }} />
            <span className="font-display uppercase text-sm leading-tight"
                  style={{ color: NAVY, letterSpacing: "-0.01em" }}>
              {summary.top_score.score}/100 on{" "}
              <span className="rm-text-orange">{summary.top_score.role}</span>
            </span>
            <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                  style={{ color: "#475569" }}>
              · {summary.top_score.company} · share this kit
            </span>
          </div>
          <Link to={`/results/${summary.top_score.analysis_id}`}
                className="rm-btn rm-btn-primary text-xs px-3 py-2 inline-flex"
                data-testid="top-score-open-btn">
            Open <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, accent, icon, testid, subtitle }) {
  return (
    <div className="rm-card-static p-2.5"
         style={{ background: "white", borderColor: "#E2E8F0", boxShadow: `2px 2px 0 0 ${accent}` }}
         data-testid={testid}>
      <div className="font-mono-rm uppercase text-[9px] tracking-widest inline-flex items-center gap-1"
           style={{ color: accent, fontWeight: 700 }}>
        {icon} {label}
      </div>
      <div className="font-display leading-none mt-1.5"
           style={{ fontSize: "1.4rem", color: NAVY, letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {subtitle && (
        <div className="text-[10px] mt-0.5 truncate" style={{ color: "#94A3B8" }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function BucketRow({ label, count, total, color, testid }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1" data-testid={testid}>
      <span className="font-mono-rm text-[10px] uppercase tracking-widest flex-shrink-0 w-32"
            style={{ color, fontWeight: 700 }}>
        {label}
      </span>
      <div className="flex-1 h-2 rounded overflow-hidden" style={{ background: "#F1F5F9" }}>
        <div className="h-full"
             style={{ width: `${pct}%`, background: color, transition: "width 0.6s ease" }} />
      </div>
      <span className="font-mono-rm text-[10px] uppercase tracking-widest flex-shrink-0 w-12 text-right"
            style={{ color: "#475569", fontWeight: 700 }}>
        {count} ({pct}%)
      </span>
    </div>
  );
}
